import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3, TrendingUp, TrendingDown, Ship, Users, Wallet, ExternalLink, FileDown, CalendarRange,
} from 'lucide-react';
import ErpShell from '../components/ErpShell';
import { ErpPageHeader } from '../components/ErpPageHeader';
import { api } from '../contexts/AuthContext';

const fmtMoney = (v) => `₪${(Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtMonth = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
};

export default function ErpReports() {
  const [summary, setSummary] = useState({ bankBalance: 0, cashBalance: 0, totalBalance: 0, chartData: [] });
  const [customers, setCustomers] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [bankTxns, setBankTxns] = useState([]);
  const [cashTxns, setCashTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, c, sh, b, ca] = await Promise.all([
          api.get('/financial-tracker-summary').catch(() => ({ data: {} })),
          api.get('/customers').catch(() => ({ data: { customers: [] } })),
          api.get('/shipment-tracking').catch(() => ({ data: { shipments: [] } })),
          api.get('/financial-tracker/bank').catch(() => ({ data: { transactions: [] } })),
          api.get('/financial-tracker/cash').catch(() => ({ data: { transactions: [] } })),
        ]);
        setSummary(s.data || {});
        setCustomers(c.data.customers || []);
        setShipments(sh.data.shipments || []);
        setBankTxns(b.data.transactions || []);
        setCashTxns(ca.data.transactions || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const chart = summary.chartData || [];
  const maxVal = useMemo(() => Math.max(1, ...chart.flatMap((c) => [c.income, c.expense])), [chart]);

  const totals = useMemo(() => {
    const all = [...bankTxns, ...cashTxns];
    let income = 0, expense = 0;
    all.forEach((t) => {
      const amt = Number(t.amount || 0);
      if (t.transactionType === 'income') income += amt; else expense += amt;
    });
    return { income, expense, profit: income - expense };
  }, [bankTxns, cashTxns]);

  const topCategories = useMemo(() => {
    const map = {};
    [...bankTxns, ...cashTxns].forEach((t) => {
      if (t.transactionType !== 'expense') return;
      const k = t.category || 'Other';
      map[k] = (map[k] || 0) + Number(t.amount || 0);
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [bankTxns, cashTxns]);

  const topCustomers = useMemo(() => {
    return [...customers].sort((a, b) => (b.shipmentCount || 0) - (a.shipmentCount || 0)).slice(0, 6);
  }, [customers]);

  const shipmentStatusCount = useMemo(() => {
    const map = {};
    shipments.forEach((s) => { map[s.status] = (map[s.status] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [shipments]);

  return (
    <ErpShell title="Reports">
      <ErpPageHeader
        eyebrow="Analytics"
        title="Reports"
        subtitle="Profit, expenses, growth — the story of your business."
        icon={BarChart3}
      />

      {/* KPI trio */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <ReportKpi tone="emerald" icon={TrendingUp} label="Total income" value={fmtMoney(totals.income)} />
        <ReportKpi tone="danger" icon={TrendingDown} label="Total expenses" value={fmtMoney(totals.expense)} />
        <ReportKpi tone="accent" icon={Wallet} label="Net profit" value={fmtMoney(totals.profit)} />
        <ReportKpi tone="info" icon={Ship} label="Shipments" value={shipments.length} sub={`${customers.length} customers`} />
      </div>

      <MonthlyProfitSection />

      {/* Monthly chart */}
      <div className="gg-card p-6 mb-5" data-testid="monthly-chart">
        <div className="flex items-baseline justify-between mb-5">
          <div>
            <p className="gg-eyebrow">Trend</p>
            <h2 className="gg-section-title mt-1" style={{ fontSize: 20 }}>Monthly income vs expenses</h2>
          </div>
        </div>
        {loading ? (
          <div className="h-56 animate-pulse rounded-xl" style={{ background: 'var(--gg-surface-2)' }} />
        ) : chart.length === 0 ? (
          <p className="text-sm text-center py-10" style={{ color: 'var(--gg-text-dim)' }}>No monthly data yet.</p>
        ) : (
          <div className="flex items-end gap-4 h-56 overflow-x-auto pb-2">
            {chart.slice(-12).map((d) => {
              const hIn = (d.income / maxVal) * 100;
              const hEx = (d.expense / maxVal) * 100;
              return (
                <div key={d.month} className="flex flex-col items-center gap-2 min-w-[54px]">
                  <div className="flex items-end gap-1 h-40">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${hIn}%` }}
                      transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
                      className="w-4 rounded-t-lg"
                      style={{ background: 'linear-gradient(180deg, #34C759, #30D158)' }}
                      title={`Income ${fmtMoney(d.income)}`}
                    />
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${hEx}%` }}
                      transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1], delay: 0.1 }}
                      className="w-4 rounded-t-lg"
                      style={{ background: 'linear-gradient(180deg, #C9A227, #FF3B30)' }}
                      title={`Expense ${fmtMoney(d.expense)}`}
                    />
                  </div>
                  <span className="text-[10px] gg-mono" style={{ color: 'var(--gg-text-dim)' }}>{fmtMonth(d.month)}</span>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex gap-6 mt-4 text-xs" style={{ color: 'var(--gg-text-dim)' }}>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded" style={{ background: '#34C759' }} /> Income
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded" style={{ background: '#C9A227' }} /> Expenses
          </span>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top customers */}
        <div className="gg-card p-6" data-testid="top-customers">
          <p className="gg-eyebrow"><Users size={11} className="inline me-1" /> Customers</p>
          <h3 className="gg-section-title mt-1 mb-4" style={{ fontSize: 18 }}>Top by shipment count</h3>
          {topCustomers.length === 0 ? (
            <p className="text-sm py-4" style={{ color: 'var(--gg-text-dim)' }}>No customers yet.</p>
          ) : (
            <div className="space-y-3">
              {topCustomers.map((c, i) => {
                const max = topCustomers[0]?.shipmentCount || 1;
                const pct = ((c.shipmentCount || 0) / max) * 100;
                return (
                  <div key={c.id} data-testid={`top-customer-${i}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="truncate font-semibold" style={{ color: 'var(--gg-text)' }}>{c.name}</span>
                      <span className="gg-tabular" style={{ color: 'var(--gg-text-dim)' }}>{c.shipmentCount || 0}</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--gg-surface-2)' }}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.7, delay: i * 0.05 }}
                        className="h-full rounded-full"
                        style={{ background: 'linear-gradient(90deg, #C9A227, #E2C15B)' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Expense categories */}
        <div className="gg-card p-6" data-testid="expense-breakdown">
          <p className="gg-eyebrow"><Wallet size={11} className="inline me-1" /> Expenses</p>
          <h3 className="gg-section-title mt-1 mb-4" style={{ fontSize: 18 }}>Where money goes</h3>
          {topCategories.length === 0 ? (
            <p className="text-sm py-4" style={{ color: 'var(--gg-text-dim)' }}>No expenses logged yet.</p>
          ) : (
            <div className="space-y-3">
              {topCategories.map(([cat, amt], i) => {
                const max = topCategories[0][1] || 1;
                const pct = (amt / max) * 100;
                const palette = ['#C9A227', '#007AFF', '#34C759', '#FF9500', '#AF52DE', '#FF3B30'];
                return (
                  <div key={cat} data-testid={`expense-cat-${i}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="truncate font-semibold" style={{ color: 'var(--gg-text)' }}>{cat}</span>
                      <span className="gg-tabular" style={{ color: 'var(--gg-text-dim)' }}>{fmtMoney(amt)}</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--gg-surface-2)' }}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.7, delay: i * 0.05 }}
                        className="h-full rounded-full"
                        style={{ background: palette[i % palette.length] }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Shipment status */}
        <div className="gg-card p-6 lg:col-span-2" data-testid="shipment-status">
          <p className="gg-eyebrow"><Ship size={11} className="inline me-1" /> Operations</p>
          <h3 className="gg-section-title mt-1 mb-4" style={{ fontSize: 18 }}>Shipment status distribution</h3>
          {shipmentStatusCount.length === 0 ? (
            <p className="text-sm py-4" style={{ color: 'var(--gg-text-dim)' }}>No shipments yet.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {shipmentStatusCount.map(([status, count], i) => (
                <div key={status} className="p-4 rounded-2xl" style={{ background: 'var(--gg-surface-2)' }} data-testid={`status-${i}`}>
                  <p className="gg-eyebrow" style={{ fontSize: 9 }}>{status}</p>
                  <p className="gg-tabular mt-1" style={{ fontSize: 24, fontWeight: 800, color: 'var(--gg-text)' }}>{count}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ErpShell>
  );
}

const ReportKpi = ({ icon: Icon, tone, label, value, sub }) => (
  <div className="gg-card p-5">
    <div className="flex items-start gap-3">
      <span className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `var(--gg-${tone}-soft)`, color: `var(--gg-${tone})` }}>
        <Icon size={18} />
      </span>
      <div>
        <p className="gg-eyebrow">{label}</p>
        <p className="gg-kpi-value gg-tabular mt-1" style={{ fontSize: 26 }}>{value}</p>
        {sub && <p className="text-xs mt-1" style={{ color: 'var(--gg-text-dim)' }}>{sub}</p>}
      </div>
    </div>
  </div>
);

function MonthlyProfitSection() {
  const [data, setData] = useState({ months: [], total: null });
  const [selected, setSelected] = useState('All');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get('/reports/profit-summary').then((r) => setData(r.data)).catch(() => {});
  }, []);

  const current = selected === 'All'
    ? data.total && { ...data.total, month: 'All' }
    : data.months.find((m) => m.month === selected);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/reports/profit-summary/pdf?month=${encodeURIComponent(selected)}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Profit_Report_${selected.replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  };

  return (
    <div className="gg-card p-6 mb-5" data-testid="monthly-profit-report">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <p className="gg-eyebrow"><CalendarRange size={11} className="inline me-1" /> Monthly</p>
          <h2 className="gg-section-title mt-1" style={{ fontSize: 20 }}>Profit Report</h2>
        </div>
        <div className="flex gap-2">
          <select value={selected} onChange={(e) => setSelected(e.target.value)} className="gg-input" style={{ width: 160 }} data-testid="profit-month-select">
            <option value="All">All months</option>
            {data.months.map((m) => <option key={m.month} value={m.month}>{m.month}</option>)}
          </select>
          <button onClick={downloadPdf} disabled={downloading} className="gg-btn-primary" data-testid="profit-pdf-btn">
            <FileDown size={14} /> {downloading ? 'Generating…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {current ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="rounded-2xl p-4" style={{ background: 'var(--gg-surface-2)' }}>
            <p className="gg-eyebrow">Records</p>
            <p className="gg-tabular mt-1" style={{ fontSize: 22, fontWeight: 800, color: 'var(--gg-text)' }} data-testid="profit-records">{current.count}</p>
          </div>
          <div className="rounded-2xl p-4" style={{ background: 'rgba(255,59,48,0.08)' }}>
            <p className="gg-eyebrow" style={{ color: 'var(--gg-danger)' }}>Cost (we pay)</p>
            <p className="gg-tabular mt-1" style={{ fontSize: 22, fontWeight: 800, color: 'var(--gg-danger)' }} data-testid="profit-cost">{fmtMoney(current.cost)}</p>
          </div>
          <div className="rounded-2xl p-4" style={{ background: 'var(--gg-surface-2)' }}>
            <p className="gg-eyebrow">Income</p>
            <p className="gg-tabular mt-1" style={{ fontSize: 22, fontWeight: 800, color: 'var(--gg-text)' }} data-testid="profit-income">{fmtMoney(current.income)}</p>
          </div>
          <div className="rounded-2xl p-4" style={{ background: 'var(--gg-emerald-soft)' }}>
            <p className="gg-eyebrow" style={{ color: 'var(--gg-emerald)' }}>Profit</p>
            <p className="gg-tabular mt-1" style={{ fontSize: 22, fontWeight: 800, color: current.profit >= 0 ? 'var(--gg-emerald)' : 'var(--gg-danger)' }} data-testid="profit-value">{fmtMoney(current.profit)}</p>
          </div>
        </div>
      ) : (
        <p className="text-sm py-4" style={{ color: 'var(--gg-text-dim)' }}>No records yet.</p>
      )}

      {data.months.length > 0 && (
        <div className="overflow-x-auto">
          <table className="gg-table" data-testid="profit-months-table">
            <thead>
              <tr><th>Month</th><th>Records</th><th>Cost</th><th>Income</th><th>Profit</th></tr>
            </thead>
            <tbody>
              {data.months.map((m) => (
                <tr key={m.month} style={selected === m.month ? { background: 'var(--gg-accent-soft)' } : undefined}>
                  <td className="font-semibold">{m.month}</td>
                  <td className="gg-tabular">{m.count}</td>
                  <td className="gg-tabular" style={{ color: 'var(--gg-danger)' }}>{fmtMoney(m.cost)}</td>
                  <td className="gg-tabular">{fmtMoney(m.income)}</td>
                  <td className="gg-tabular font-bold" style={{ color: m.profit >= 0 ? 'var(--gg-emerald)' : 'var(--gg-danger)' }}>{fmtMoney(m.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
